import { useState, useEffect } from "react";
import { getSectionBrands } from "../../services/brandService";

// ─── Config ───────────────────────────────────────────────────────────────────
const CONSTANT = 11.664;

const GOLD_CATEGORIES = [
  { key: "pc", label: "PC" },
  { key: "lab", label: "Lab", noBrands: true },
  { key: "bar", label: "Bar" },
  { key: "coin", label: "Coin" },
  { key: "ginni", label: "Ginni", noBrands: true },
];
const SILVER_CATEGORIES = [{ key: "silver", label: "Silver" }];

const makeRow = (noBrands = false) => ({
  id: Math.random().toString(36).slice(2),
  brand: "plzSelect",
  brandId: "",
  brandName: "",
  qty: "1",
  weight: "",
  totalWeight: "",
  isManualTotal: false,
  unit: "gram",
  noBrands,
});

// ─── Brand cache ──────────────────────────────────────────────────────────────
const brandCache = {};
async function fetchBrands(section) {
  if (brandCache[section]) return brandCache[section];
  try {
    const res = await getSectionBrands(section);
    if (res.success) {
      brandCache[section] = res.brands || [];
      return brandCache[section];
    }
    return [];
  } catch {
    return [];
  }
}

const toGrams = (w, unit) =>
  (parseFloat(w) || 0) * (unit === "tola" ? CONSTANT : 1);

// ─── Field wrapper ────────────────────────────────────────────────────────────
function Field({ label, required, error, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {label && (
        <label className="form-label" style={{ fontSize: 12, marginBottom: 3 }}>
          {label}
          {required && <span className="text-danger ms-1">*</span>}
        </label>
      )}
      {children}
      <div style={{ minHeight: 16, marginTop: 2 }}>
        {error && (
          <span style={{ fontSize: 11, color: "#dc3545" }}>{error}</span>
        )}
      </div>
    </div>
  );
}

// ─── Brand row ────────────────────────────────────────────────────────────────
function BrandRow({
  row,
  brands,
  onChange,
  onRemove,
  canRemove,
  errors,
  noBrands,
  onClearError,
}) {
  const stopScroll = (e) => e.target.blur();

  const set = (field, val) => {
    let updated = { ...row, [field]: val };

    // ✅ Auto calculate total if NOT manually edited
    if (!row.isManualTotal && (field === "qty" || field === "weight")) {
      const qty = parseFloat(field === "qty" ? val : updated.qty || 0);
      const weight = parseFloat(field === "weight" ? val : updated.weight || 0);

      updated.totalWeight = (qty * weight).toString();
    }

    onChange(updated);

    if (errors?.[field]) onClearError(field);
  };

  const calculatedTotal =
    parseFloat(row.qty || 0) * parseFloat(row.weight || 0);
  const finalWeight = row.isManualTotal
    ? parseFloat(row.totalWeight || 0)
    : calculatedTotal;

  const grams = toGrams(finalWeight, row.unit);

  return (
    <div
      className="p-2 rounded mb-2"
      style={{
        background: "#fff",
        border: `1px solid ${Object.keys(errors || {}).length ? "#f5c2c7" : "#dee2e6"}`,
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: noBrands
            ? "2fr 2.5fr 1.5fr auto"
            : "2.5fr 1.5fr 2fr 1.5fr auto",
          gap: 8,
          alignItems: "end",
        }}
      >
        {!noBrands && (
          <Field label="Brand" required error={errors?.brand}>
            <select
              className={`form-control form-control-sm ${errors?.brand ? "is-invalid" : ""}`}
              value={row.brand}
              onChange={(e) => {
                const selected = brands.find((b) => b._id === e.target.value);
                onChange({
                  ...row,
                  brand: e.target.value,
                  brandId: e.target.value,
                  brandName: selected?.name ?? "",
                });
                if (errors?.brand) onClearError("brand");
              }}
            >
              <option value="plzSelect">--- Select ---</option>
              {brands.length === 0 && row.brandId && row.brandName && (
                <option value={row.brandId}>{row.brandName}</option>
              )}
              {brands.map((b) => (
                <option key={b._id} value={b._id}>
                  {b.name}
                </option>
              ))}
            </select>
          </Field>
        )}

        <Field label="Qty">
          <input
            type="number"
            className="form-control form-control-sm"
            placeholder="1"
            min={1}
            step={1}
            value={row.qty}
            onWheel={stopScroll}
            onChange={(e) => set("qty", e.target.value)}
          />
        </Field>

        <Field label="Weight" required error={errors?.weight}>
          <input
            type="number"
            className={`form-control form-control-sm ${errors?.weight ? "is-invalid" : ""}`}
            placeholder="0.000"
            min={0}
            step="0.001"
            value={row.weight}
            onWheel={stopScroll}
            onChange={(e) => set("weight", e.target.value)}
          />
        </Field>

        <Field label="Unit">
          <select
            className="form-control form-control-sm"
            value={row.unit}
            onChange={(e) => set("unit", e.target.value)}
          >
            <option value="gram">gram</option>
            <option value="tola">tola</option>
          </select>
        </Field>
        <Field label="Total Weight">
          <input
            type="number"
            className="form-control form-control-sm"
            placeholder="0.000"
            value={row.totalWeight}
            onChange={(e) => {
              set("totalWeight", e.target.value);
              onChange({
                ...row,
                totalWeight: e.target.value,
                isManualTotal: true, // ✅ mark manual edit
              });
            }}
          />
        </Field>

        <div
          style={{
            paddingBottom: 18,
            display: "flex",
            alignItems: "center",
            gap: 5,
          }}
        >
          {grams > 0 && (
            <span
              style={{
                fontSize: 11,
                background: "#dbe8ff",
                color: "#0d6efd",
                borderRadius: 10,
                padding: "2px 7px",
                whiteSpace: "nowrap",
              }}
            >
              {grams.toFixed(3)} g
            </span>
          )}
          {canRemove ? (
            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              style={{ fontSize: 11, padding: "3px 7px" }}
              onClick={onRemove}
            >
              ✕
            </button>
          ) : (
            <div style={{ width: 30 }} />
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Single category block ────────────────────────────────────────────────────
function CategoryBlock({
  cat,
  rows,
  brands,
  onChange,
  onRemove,
  rowErrors,
  onClearRowError,
}) {
  const addRow = () => onChange([...rows, makeRow(cat.noBrands ?? false)]);
  const updateRow = (idx, upd) =>
    onChange(rows.map((r, i) => (i === idx ? upd : r)));
  const removeRow = (idx) => onChange(rows.filter((_, i) => i !== idx));
  const totalG = rows.reduce((s, r) => s + toGrams(r.weight, r.unit), 0);

  return (
    <div
      className="mb-3 p-3 rounded"
      style={{ border: "1px solid #c2d3f8", background: "#f0f4ff" }}
    >
      <div className="d-flex align-items-center justify-content-between mb-2">
        <div className="d-flex align-items-center gap-2">
          <span
            className="fw-semibold"
            style={{ fontSize: 14, color: "#0d6efd" }}
          >
            {cat.label}
          </span>
          {totalG > 0 && (
            <span
              style={{
                fontSize: 11,
                background: "#dbe8ff",
                color: "#0d6efd",
                borderRadius: 10,
                padding: "1px 8px",
              }}
            >
              {totalG.toFixed(3)} g
            </span>
          )}
        </div>
        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            style={{ fontSize: 12, padding: "2px 10px" }}
            onClick={addRow}
          >
            + Row
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-danger"
            style={{ fontSize: 12, padding: "2px 8px" }}
            onClick={onRemove}
          >
            ✕
          </button>
        </div>
      </div>

      {rows.map((row, idx) => (
        <BrandRow
          key={row.id}
          row={row}
          brands={brands}
          noBrands={cat.noBrands}
          onChange={(upd) => updateRow(idx, upd)}
          onRemove={() => removeRow(idx)}
          canRemove={rows.length > 1}
          errors={rowErrors?.[row.id]}
          onClearError={(field) => onClearRowError(row.id, field)}
        />
      ))}
    </div>
  );
}

// ─── Main modal ───────────────────────────────────────────────────────────────
export default function AddInventory({
  show,
  onClose,
  onSave,
  onUpdate,
  isLoading = false,
  editData = null,
}) {
  const isEditMode = !!editData?._id;

  const [type, setType] = useState("plzSelect");
  const [categories, setCategories] = useState([]); // [{ catKey, rows }]
  const [brandMap, setBrandMap] = useState({});
  const [cashAmount, setCashAmount] = useState("");
  const [errors, setErrors] = useState({});
  const [rowErrors, setRowErrors] = useState({});

  // ── Prefill (edit mode) ─────────────────────────────────────────────────────
  useEffect(() => {
    if (show && isEditMode) {
      const inv = editData;
      setType(inv.type ?? "plzSelect");
      setCashAmount(inv.cashAmount ? String(inv.cashAmount) : "");
      if (inv.categories?.length) {
        const allCats = [...GOLD_CATEGORIES, ...SILVER_CATEGORIES];
        setCategories(
          inv.categories.map((cat) => {
            const catConfig = allCats.find((c) => c.key === cat.catKey);
            const nb = catConfig?.noBrands ?? false;
            return {
              catKey: cat.catKey,
              rows: (cat.rows ?? []).map((r) => ({
                id: Math.random().toString(36).slice(2),
                brand: r.brandId ?? r.brand?._id ?? "plzSelect",
                brandId: r.brandId ?? r.brand?._id ?? "",
                brandName: r.brandName ?? r.brand?.name ?? "",
                qty: String(r.qty ?? 1),
                weight: String(r.weight ?? ""),
                totalWeight: String(r.totalWeight ?? ""),
                unit: r.unit ?? "gram",
                noBrands: nb,
              })),
            };
          }),
        );
      } else {
        setCategories([]);
      }
      setErrors({});
      setRowErrors({});
    } else if (show && !isEditMode) {
      resetAll();
    }
  }, [show, editData?._id]);

  // ── Load brands whenever categories change ──────────────────────────────────
  useEffect(() => {
    categories.forEach(({ catKey }) => {
      if (!brandMap[catKey]) {
        fetchBrands(catKey).then((b) =>
          setBrandMap((p) => ({ ...p, [catKey]: b })),
        );
      }
    });
  }, [categories.map((c) => c.catKey).join(",")]);

  const isMetal = type === "gold" || type === "silver";
  const isCash = type === "cash";
  const catList =
    type === "gold"
      ? GOLD_CATEGORIES
      : type === "silver"
        ? SILVER_CATEGORIES
        : [];
  const addedKeys = categories.map((c) => c.catKey);

  const toggleCategory = (cat) => {
    if (addedKeys.includes(cat.key)) {
      // remove
      setCategories((p) => p.filter((c) => c.catKey !== cat.key));
    } else {
      // add
      setCategories((p) => [
        ...p,
        { catKey: cat.key, rows: [makeRow(cat.noBrands ?? false)] },
      ]);
      setErrors((p) => {
        const n = { ...p };
        delete n.categories;
        return n;
      });
    }
  };

  const updateRows = (catKey, rows) =>
    setCategories((p) =>
      p.map((c) => (c.catKey === catKey ? { ...c, rows } : c)),
    );

  const clearRowError = (rowId, field) => {
    setRowErrors((prev) => {
      if (!prev[rowId]?.[field]) return prev;
      const next = { ...prev };
      const rowNext = { ...next[rowId] };
      delete rowNext[field];
      if (!Object.keys(rowNext).length) delete next[rowId];
      else next[rowId] = rowNext;
      return next;
    });
  };

  // ── Validation ──────────────────────────────────────────────────────────────
  const validate = () => {
    const e = {},
      rErrs = {};
    if (type === "plzSelect") {
      e.type = "Select a type.";
      return { e, rErrs };
    }

    if (isCash) {
      if (!cashAmount || parseFloat(cashAmount) <= 0)
        e.cashAmount = "Enter an amount.";
      return { e, rErrs };
    }

    if (!categories.length) {
      e.categories = "Select at least one category.";
      return { e, rErrs };
    }

    categories.forEach(({ rows }) => {
      rows.forEach((row) => {
        const rowE = {};
        if (!row.noBrands && (!row.brand || row.brand === "plzSelect"))
          rowE.brand = "Select a brand.";
        if (!row.weight || parseFloat(row.weight) <= 0)
          rowE.weight = "Enter weight.";
        if (Object.keys(rowE).length) rErrs[row.id] = rowE;
      });
    });

    return { e, rErrs };
  };

  // ── Save ────────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    const { e, rErrs } = validate();
    setErrors(e);
    setRowErrors(rErrs);
    if (Object.keys(e).length || Object.keys(rErrs).length) return;

    let payload;
    if (isCash) {
      payload = { type, cashAmount: parseFloat(cashAmount) };
    } else {
      const totalWeight = categories
        .flatMap((c) => c.rows)
        .reduce((s, r) => s + toGrams(r.weight, r.unit), 0);
      payload = { type, categories, totalWeight };
    }

    let ok = false;
    if (isEditMode) {
      if (onUpdate) ok = await onUpdate(editData._id, payload);
    } else {
      if (onSave) ok = await onSave(payload);
    }

    if (ok) {
      resetAll();
      onClose();
    } else setErrors({ global: "Couldn't save. Please try again." });
  };

  const resetAll = () => {
    setType("plzSelect");
    setCategories([]);
    setBrandMap({});
    setCashAmount("");
    setErrors({});
    setRowErrors({});
  };

  const handleClose = () => {
    resetAll();
    onClose();
  };

  if (!show) return null;

  const grandG = categories
    .flatMap((c) => c.rows)
    .reduce((s, r) => s + toGrams(r.totalWeight, r.unit), 0);

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(0,0,0,0.5)",
        overflowY: "auto",
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        padding: "4rem 1rem 2rem",
      }}
      onClick={(e) => e.target === e.currentTarget && handleClose()}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 620,
          background: "#fff",
          borderRadius: 8,
          overflow: "hidden",
        }}
      >
        {/* HEADER */}
        <div className="modal-header">
          <h1 className="modal-title fs-5">
            {isEditMode ? "Edit Inventory" : "Add Inventory"}
          </h1>
          {isEditMode && (
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                textTransform: "uppercase",
                background: "#fff8e1",
                color: "#b45309",
                border: "1px solid #fde68a",
                borderRadius: 6,
                padding: "2px 10px",
                marginLeft: 8,
              }}
            >
              Editing
            </span>
          )}
          <button className="btn-close ms-auto" onClick={handleClose} />
        </div>

        {/* BODY */}
        <div className="modal-body">
          {errors.global && (
            <div className="alert alert-danger py-2 mb-3">{errors.global}</div>
          )}

          {/* ── Type selector ── */}
          <div className="mb-3">
            <label className="form-label">Inventory Type</label>
            <div className="d-flex gap-2 flex-wrap">
              {[
                { val: "gold", label: "Gold" },
                { val: "silver", label: "Silver" },
                { val: "cash", label: "Cash" },
              ].map(({ val, label }) => (
                <button
                  key={val}
                  type="button"
                  disabled={isEditMode}
                  className={`btn btn-sm ${type === val ? "btn-primary" : "btn-outline-secondary"}`}
                  style={{
                    borderRadius: 20,
                    padding: "4px 18px",
                    fontSize: 13,
                  }}
                  onClick={() => {
                    setType(val);
                    setCategories([]);
                    setCashAmount("");
                    setErrors({});
                    setRowErrors({});
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
            {errors.type && (
              <div style={{ fontSize: 11, color: "#dc3545", marginTop: 4 }}>
                {errors.type}
              </div>
            )}
            {isEditMode && (
              <div className="form-text" style={{ fontSize: 11 }}>
                Cannot change on edit.
              </div>
            )}
          </div>

          {/* ── Cash amount ── */}
          {isCash && (
            <div className="row g-3">
              <div className="col-6">
                <label className="form-label">
                  Amount (PKR) <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  className={`form-control ${errors.cashAmount ? "is-invalid" : ""}`}
                  placeholder="e.g. 500000"
                  min={0}
                  step="1"
                  value={cashAmount}
                  onWheel={(e) => e.target.blur()}
                  onChange={(e) => {
                    setCashAmount(e.target.value);
                    setErrors((p) => {
                      const n = { ...p };
                      delete n.cashAmount;
                      return n;
                    });
                  }}
                />
                {errors.cashAmount && (
                  <div className="invalid-feedback">{errors.cashAmount}</div>
                )}
              </div>
            </div>
          )}

          {/* ── Category pills + blocks (gold / silver) ── */}
          {isMetal && (
            <>
              <div className="mb-3">
                <label className="form-label">
                  Categories
                  {errors.categories && (
                    <span
                      style={{ fontSize: 11, color: "#dc3545", marginLeft: 8 }}
                    >
                      {errors.categories}
                    </span>
                  )}
                </label>
                <div className="d-flex gap-2 flex-wrap">
                  {catList.map((cat) => {
                    const active = addedKeys.includes(cat.key);
                    return (
                      <button
                        key={cat.key}
                        type="button"
                        className={`btn btn-sm ${active ? "btn-primary" : "btn-outline-secondary"}`}
                        style={{
                          borderRadius: 20,
                          padding: "4px 16px",
                          fontSize: 13,
                        }}
                        onClick={() => toggleCategory(cat)}
                      >
                        {active ? `✓ ${cat.label}` : `+ ${cat.label}`}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Category blocks */}
              {categories.map(({ catKey, rows }) => {
                const cat = catList.find((c) => c.key === catKey);
                const brands = brandMap[catKey] ?? [];
                console.log(catKey, brands);
                return (
                  <CategoryBlock
                    key={catKey}
                    cat={cat}
                    rows={rows}
                    brands={brands}
                    onChange={(upd) => updateRows(catKey, upd)}
                    onRemove={() => toggleCategory(cat)}
                    rowErrors={rowErrors}
                    onClearRowError={clearRowError}
                  />
                );
              })}

              {/* Grand total */}
              {grandG > 0 && (
                <div
                  className="p-2 rounded"
                  style={{ background: "#e8f5e9", border: "1px solid #c8e6c9" }}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="fw-semibold" style={{ fontSize: 13 }}>
                      Total Weight
                    </span>
                    <strong style={{ color: "#0d6efd", fontSize: 13 }}>
                      {grandG.toFixed(3)} g
                    </strong>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* FOOTER */}
        <div className="modal-footer m-2 gap-2">
          <button className="btn btn-secondary" onClick={handleClose}>
            Close
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={isLoading || type === "plzSelect"}
          >
            {isLoading ? "Saving..." : isEditMode ? "Save Changes" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
